import * as React from 'react';

import { ApplicationState, ConnectedReduxProps } from '../../store';
import { BelfiusButton, BelfiusTextInput } from '../../components/custom';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { getI18n } from '../../store/utils/i18n/strings.manager';
import { loginUserRequest } from '../../store/login';
import { environment } from '../../environment/environment';
import styles from './Login.module.css';
import { generatePKCE, generateRandomString } from '../../utils/helper/pkce';

interface PropsFromDispatch {
  readonly loginUserRequest: typeof loginUserRequest;
}

interface PropsFromState {
  readonly errorMessage: string;
  readonly showLoader: boolean;
}

interface State {
  readonly username: string;
  readonly password: string;
  readonly offlineText: string;
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps;

class Login extends React.Component<AllProps, State> {
  constructor(props: AllProps) {
    super(props);
    this.state = {
      username: '',
      password: '',
      offlineText: ''
    };
  }

  async componentDidMount() {
    if (!environment.useOkta) {
      return;
    }

    const isCallback = window.location.pathname.includes('/implicit/callback');
    if (isCallback) return;

    const urlParams = new URLSearchParams(window.location.search);
    const isLoggedOut = urlParams.get("loggedOut") === "true";

    if (isLoggedOut) {
      // User came from logout — show login page normally
      return;
    }

    const hasSilentFailed = window.location.hash.includes('oktaSilentFailed');
    if (hasSilentFailed) {
      await this.userLoginOKTA();
    } else {
      await this.userLoginOKTASilently();
    }
  }

  userLogin = () => {
    this.props.loginUserRequest(this.state.username, this.state.password);
  }

  userLoginOKTA = async () => {
    // tslint:disable-next-line:max-line-length
    
    // New Okta authorize request for PKCE flow
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateRandomString(32);
    const nonce = generateRandomString(16);
    
    // Store verifier in sessionStorage for later exchange
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('pkce_state', state);
    sessionStorage.setItem('pkce_nonce', nonce);

    const oktaAuthorizeRequest = 
          `${environment.oktaAuthorizeUrl}` +
          `?client_id=${environment.oktaClientId}` +
          `&response_type=code` +           
          `&scope=openid offline_access` +
          `&redirect_uri=${window.location.origin}/implicit/callback` +
          `&state=${state}` +
          `&nonce=${nonce}` +
          `&prompt=login` +
          `&code_challenge=${codeChallenge}` +  
          `&code_challenge_method=S256`;

    window.location.replace(oktaAuthorizeRequest);
  }

  userLoginOKTASilently = async () => {
    // tslint:disable-next-line:max-line-length
    // tslint:disable-next-line:max-line-length
    sessionStorage.setItem('pkce_silent', 'true');

    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateRandomString(32);
    const nonce = generateRandomString(16);

    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('pkce_state', state);
    sessionStorage.setItem('pkce_nonce', nonce);

    const oktaAuthorizeRequest =
          `${environment.oktaAuthorizeUrl}` +
          `?client_id=${environment.oktaClientId}` +
          `&response_type=code` +       
          `&scope=openid offline_access` +
          `&redirect_uri=${window.location.origin}/implicit/callback` +
          `&state=${state}` +
          `&nonce=${nonce}` +
          `&prompt=none` +              
          `&code_challenge=${codeChallenge}` +   
          `&code_challenge_method=S256`;

    window.location.replace(oktaAuthorizeRequest);
  }

  public render() {
    const strings = getI18n();

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}>
              <a href='http://www.zamia.be' target='_blank' rel="noreferrer">
                <img className={styles.logo} alt="" src={require('../../assets/images/login.png')} />
              </a>
            </div>
          </div>
          <div id='loader' className={styles.loadingContainer}>
            <div className={styles.logo}>
              <img hidden={!this.props.showLoader} alt="" src={require('../../assets/images/spinnerLoaderBlue.gif')} />
            </div>
          </div>
          <div className={styles.formContainer}>
              <BelfiusTextInput
                className={styles.username}
                value={this.state.username}
                placeholder={strings.get('login.username')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  this.setState({ ...this.state, username: event.target.value })}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    this.userLogin();
                  }
                }} />

              <BelfiusTextInput
                type='password'
                className={styles.password}
                value={this.state.password}
                placeholder={strings.get('login.password')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  this.setState({ ...this.state, password: event.target.value })}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    this.userLogin();
                  }
                }} />
              < span className={styles.errorMessage} > {strings.get(this.props.errorMessage)}</ span>
              <BelfiusButton
                className={styles.loginButton}
                onClick={() => this.userLogin()}
                title={strings.get('login.login')} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ login }: ApplicationState): PropsFromState => ({
  errorMessage: login.errorMessage,
  showLoader: login.showLoader
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  loginUserRequest: (username: string, password: string) => dispatch(loginUserRequest(username, password))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Login);
